/**
 * EntityPreviewCard — generic fallback for pending-action inputs that
 * aren't task-shaped (calendar events, people, emails, etc.).
 *
 * Instead of forcing the user to trust an opaque "Confirm" button or show
 * them raw JSON, we render a clean two-column key/value grid with the input
 * fields from the capability's input_schema (when available). The user can
 * edit the fields inline; on confirm, we POST the edited dict via
 * override_input_data.
 *
 * Field controls are chosen heuristically:
 *   - keys matching /date$/ → SmartDateField
 *   - work_area_id          → SmartWorkAreaField
 *   - priority              → SmartPriorityField
 *   - effort                → SmartEffortField
 *   - *_person_id / assignee / owner → SmartAssigneeField
 *   - boolean               → checkbox
 *   - integer               → number input
 *   - everything else       → text input (or textarea for long strings)
 *
 * This gives EVERY confirm-required capability a clean, editable in-chat
 * preview without having to ship bespoke UI per capability.
 */
import React, { useState, useCallback } from 'react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  CheckCircle2, Loader2, Sparkles, MousePointerClick, ShieldAlert,
} from 'lucide-react';
import {
  SmartWorkAreaField, SmartDateField, SmartPriorityField, SmartEffortField,
  SmartAssigneeField,
} from './SmartField';

const API = '/api';

const policyMeta = {
  conversational: { Icon: Sparkles,          tint: 'sky',    label: 'Level 2 · Affirm to run' },
  button:         { Icon: MousePointerClick, tint: 'amber',  label: 'Level 3 · Confirm to run' },
  typed:          { Icon: ShieldAlert,       tint: 'red',    label: 'Level 4 · Typed confirmation' },
};

function humanizeKey(k) {
  return k.replace(/_/g, ' ').replace(/\b(id|url)\b/gi, s => s.toUpperCase())
    .replace(/^\w/, c => c.toUpperCase());
}

function fieldKindFor(key, value) {
  if (key === 'work_area_id') return 'work_area';
  if (/(_person_id|assignee|owner_id)$/i.test(key)) return 'assignee';
  if (key === 'task_priority' || key === 'priority') return 'priority';
  if (key === 'task_estimated_effort' || key === 'effort') return 'effort';
  if (/_date$/i.test(key) || key === 'date') return 'date';
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'string' && value.length > 80) return 'textarea';
  return 'text';
}


export const EntityPreviewCard = ({ pending, onConfirmed }) => {
  const [fields, setFields] = useState(pending.input_data || {});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [phrase, setPhrase] = useState('');

  const policy = pending.confirmation_policy;
  const meta = policyMeta[policy === 'conversational' ? 'conversational' : policy === 'typed' ? 'typed' : 'button'];
  const Icon = meta.Icon;
  const tint = meta.tint;

  const update = useCallback((k, v) => setFields(prev => ({ ...prev, [k]: v })), []);

  const submit = useCallback(async () => {
    setBusy(true);
    try {
      const body = {
        override_input_data: fields,
      };
      if (policy === 'button') body.confirmation_token = pending.confirmation_token;
      if (policy === 'typed')  body.typed_phrase = phrase;
      const r = await fetch(`${API}/ai/v2/pending-actions/${pending.pending_action_id}/confirm`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (r.ok && d.success) {
        setDone(true);
        toast.success(`${pending.capability_name || pending.capability_id} confirmed`);
        onConfirmed?.(pending.pending_action_id, d);
      } else {
        const err = d.error || d;
        toast.error(err.error_code || 'AI-CONFIRM', { description: err.message || err.title || 'Confirmation failed' });
      }
    } finally { setBusy(false); }
  }, [fields, pending, onConfirmed, policy, phrase]);

  if (done) return null;

  const required = pending.required_typed_phrase;
  const keys = Object.keys(fields);

  return (
    <div
      className={cn(
        'mt-2 rounded-lg border overflow-hidden',
        tint === 'sky' && 'border-sky-300 bg-sky-50/70 dark:border-sky-900 dark:bg-sky-950/30',
        tint === 'amber' && 'border-amber-300 bg-amber-50/70 dark:border-amber-900 dark:bg-amber-950/30',
        tint === 'red' && 'border-red-400 bg-red-50/70 dark:border-red-900 dark:bg-red-950/30',
      )}
      data-testid={`chat-pending-${pending.pending_action_id}`}
    >
      <div className={cn(
        'px-3 py-2 flex items-center justify-between gap-3',
        tint === 'sky'   && 'text-sky-700 dark:text-sky-300',
        tint === 'amber' && 'text-amber-700 dark:text-amber-300',
        tint === 'red'   && 'text-red-700 dark:text-red-300',
      )}>
        <div className="flex items-center gap-1.5 text-[11px] font-medium">
          <Icon className="w-3 h-3" /> {meta.label}
        </div>
        <Badge variant="outline" className="text-[10px]">
          {pending.capability_name || pending.capability_id}
        </Badge>
      </div>

      {keys.length === 0 ? (
        <div className="px-3 pb-2 text-[11px] text-muted-foreground">
          No editable fields — hit confirm to run.
        </div>
      ) : (
        <div className="px-3 pb-2 space-y-1.5">
          {keys.map(k => {
            const value = fields[k];
            const kind = fieldKindFor(k, value);
            const id = `entity-preview-${k}-${pending.pending_action_id}`;
            return (
              <div key={k} className="grid grid-cols-[130px_1fr] items-center gap-2 text-[11px]">
                <label className="text-muted-foreground truncate" title={k}>{humanizeKey(k)}</label>
                {kind === 'work_area' ? (
                  <SmartWorkAreaField value={value} onChange={(v) => update(k, v)} testId={id} />
                ) : kind === 'assignee' ? (
                  <SmartAssigneeField value={value} onChange={(v) => update(k, v)} testId={id} />
                ) : kind === 'priority' ? (
                  <SmartPriorityField value={value} onChange={(v) => update(k, v)} testId={id} />
                ) : kind === 'effort' ? (
                  <SmartEffortField value={value} onChange={(v) => update(k, v)} testId={id} />
                ) : kind === 'date' ? (
                  <SmartDateField value={value || ''} onChange={(v) => update(k, v)} testId={id} />
                ) : kind === 'boolean' ? (
                  <input
                    type="checkbox" checked={!!value}
                    onChange={(e) => update(k, e.target.checked)}
                    data-testid={id}
                  />
                ) : kind === 'number' ? (
                  <Input
                    type="number" value={value ?? ''}
                    onChange={(e) => update(k, e.target.value === '' ? null : Number(e.target.value))}
                    className="h-7 text-[11px]"
                    data-testid={id}
                  />
                ) : kind === 'textarea' ? (
                  <Textarea
                    value={value || ''}
                    onChange={(e) => update(k, e.target.value)}
                    className="min-h-0 h-14 text-[11px] resize-y"
                    data-testid={id}
                  />
                ) : (
                  <Input
                    value={value ?? ''}
                    onChange={(e) => update(k, e.target.value)}
                    className="h-7 text-[11px]"
                    data-testid={id}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="px-3 pb-3 flex items-center justify-end gap-2">
        {policy === 'typed' && required && (
          <Input
            value={phrase}
            onChange={(e) => setPhrase(e.target.value)}
            placeholder={`Type: ${required}`}
            className="h-7 text-[11px] w-48"
            data-testid={`chat-pending-typed-input-${pending.pending_action_id}`}
          />
        )}
        <Button
          size="sm"
          className={cn(
            'h-7 text-[11px] gap-1',
            tint === 'sky'   && 'bg-sky-600 hover:bg-sky-700',
            tint === 'amber' && 'bg-amber-600 hover:bg-amber-700',
            tint === 'red'   && 'bg-red-600 hover:bg-red-700',
          )}
          onClick={submit}
          disabled={
            busy
            || (policy === 'button' && !pending.confirmation_token)
            || (policy === 'typed' && phrase !== required)
          }
          data-testid={`chat-pending-confirm-${pending.pending_action_id}`}
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
          {policy === 'conversational' ? 'Yes, run it' : 'Confirm'}
        </Button>
      </div>
    </div>
  );
};

export default EntityPreviewCard;
